/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Homepage route — _app._index.tsx
 *
 * All homepage content is authored in the "sfnext-homepage" Page Designer page
 * (headerbanner + main regions). The legacy ISML slots — home-main, home-bottom-*,
 * home-middle-* and home-recomm* (Einstein carousels) — are all replaced by
 * components placed in those regions; the route fetches no per-slot pages.
 *
 * When the page doesn't exist yet in Business Manager, `fetchPageWithComponentData`
 * returns null (404) and <Region> renders nothing.
 */

import { redirect } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Route } from './+types/_app._index';
import { Region } from '@/components/region';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { resolvePrefix } from '@salesforce/storefront-next-runtime/site-context';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';

/**
 * Page Designer metadata for the homepage.
 *
 * These region definitions tell Page Designer which editable regions exist on the
 * homepage page in Business Manager. Each entry is surfaced as a drop zone that
 * merchants can populate with components.
 */
@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero carousel, featured products, and help sections',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'topheaderbanner',
        name: 'Top Header Banner Region',
        description: 'Top area. Max 3 components.',
        maxComponents: 3,
        // This region is rendered in the shared header via <Region regionId="top-headerbanner" />.
    },
    {
        id: 'headerbanner',
        name: 'Header Banner Region',
        description: 'Hero/banner area. Max 3 components.',
        maxComponents: 3,
        // This region is rendered in the shared header via <Region regionId="headerbanner" />.
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Primary page body  (home-main slot equivalent) managed by Page Designer.',
        maxComponents: 10,
        // This region is rendered in the homepage template via <Region regionId="main" />.
    },
])
export class HomePageMetadata {}

export type HomePageData = {
    /**
     * Homepage Page Designer page (headerbanner + main regions). Awaited: it holds the above-the-fold hero (LCP)
     * and the layout of the whole page (CLS). Per-component loader data stays deferred in `componentData`.
     */
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>>;
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    ogImageUrl: string;
};

/**
 * Server-side loader.
 *
 * - The Page Designer page structure is critical (LCP hero + page layout) and is awaited, so the regions are
 *   server-rendered in the initial HTML. Streaming it previously rendered an empty main area first and injected the
 *   whole page afterwards (CLS ≈ 1.0).
 * - Non-critical data (wishlist, per-component loader data inside `page.componentData`) stays deferred.
 * - Create async work here (not in component render) to preserve Promise identity.
 */
export async function loader(args: Route.LoaderArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    // Redirect bare "/" to the default site/locale prefixed homepage
    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? config.i18n.fallbackLng;
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/`);
    }

    // Critical SEO URL metadata is resolved eagerly on the server.
    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // Start the deferred work first so it runs in parallel with the awaited page fetch. Observed so a rejection
    // can't become an unhandled rejection if nothing awaits it (consumers still see the rejection).
    const wishlistInitialState = fetchWishlistInitialState(args.context);
    wishlistInitialState.catch((error: unknown) => {
        logger.warn('HomePage: wishlist initial state failed', {
            error: error instanceof Error ? error.message : String(error),
        });
    });

    // A failed page fetch renders empty regions (as the previous deferred <Await errorElement> did) rather than
    // failing the whole route. SCAPI errors already resolve to null; this covers network errors/timeouts.
    const page = await fetchPageWithComponentData(args, { pageId: 'sfnext-homepage' }).catch((error: unknown) => {
        logger.error('HomePage: Page Designer page fetch failed', {
            error: error instanceof Error ? error.message : String(error),
        });
        return null;
    });

    return {
        page,
        wishlistInitialState,
        pageUrl,
        ogImageUrl: new URL('/images/home-banner.jpg', requestUrl.origin).href,
    };
}

/**
 * HomePage — renders the homepage Page Designer regions.
 *
 * Missing Page Designer content renders nothing until the merchant authors it.
 */
export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    const { t } = useTranslation('home');

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <div className="pb-16 -mt-8 flex flex-col lg:gap-5">
                <SeoMeta
                    rawTitle
                    title={t('meta.title')}
                    description={t('meta.description')}
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                {/*
                 * home-main slot
                 * Legacy: <isslot id="home-main" description="Flash Banner" context="global" />
                 * Modern: headerbanner region inside the homepage PD page.
                 */}
                <Region page={loaderData.page} regionId="headerbanner" />
                <Region page={loaderData.page} regionId="main" />
            </div>
        </WishlistProvider>
    );
}
